// Shared by every screen that reviews an extracted resume — bulk review, edit
// profile, reupload. They run the same form (the confirm-details steps) against
// the same job field config, so "how far is this form from saveable?" has to be
// answered the same way on all three.
//
// The answer has to agree with the zod schema in confirm-details/validation.schema.js
// on every field, or the chip says "Ready to save" and the save then fails. The
// two places that could drift are named below: `always` mirrors that schema's
// DEFAULT_REQUIRED, and `salaryGated` mirrors its `salaryFieldsRequired` flag.
// `fieldMatches` lives here and the schema imports it, so there is one matcher.

const isBlankValue = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

/**
 * True when the job marks any of `aliases` mandatory.
 *
 * Exact match, never a suffix: a job that marks `work_experience.location`
 * mandatory must not also make `personal_information.location` mandatory. Each
 * alias list below already carries both the dotted key and the bare legacy one,
 * so nothing needs a suffix to be found.
 */
export const fieldMatches = (mandatoryFields, aliases) => mandatoryFields.some((field) => aliases.includes(field));

/** True when any node in a react-hook-form error tree carries a message. */
export const hasErrorTree = (errors) => {
  if (!errors || typeof errors !== "object") return false;
  return Object.values(errors).some((value) => {
    if (!value || typeof value !== "object") return false;
    if (value.message) return true;
    return hasErrorTree(value);
  });
};

/** Dotted path of the first field with an error, for scrolling to it. */
export const getFirstErrorPath = (errors, prefix = "") => {
  for (const key of Object.keys(errors || {})) {
    const val = errors[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (val?.message) return path;
    if (val && typeof val === "object") {
      const nested = getFirstErrorPath(val, path);
      if (nested) return nested;
    }
  }
  return null;
};

// Each entry maps the aliases a job's mandatoryFields config might use for a
// field to a getter that reads that field off the form values. Driven by data
// instead of one hand-written if-block per field, so adding a newly
// configurable field is a one-line addition here rather than a new branch.
//
// `always`      — buildPersonalSchema requires it whatever the job config says
//                 (its DEFAULT_REQUIRED list), so count it whatever the config says.
// `salaryGated` — buildSalarySchema drops the rule when salaryFieldsRequired is
//                 false, so this counts only when the caller passes it true.
const MANDATORY_FIELD_MAP = [
  { aliases: ["personal_information.full_name", "personal_information.name", "name"], always: true, get: (v) => v.personal_information?.name },
  { aliases: ["personal_information.email", "email"], always: true, get: (v) => v.personal_information?.email },
  { aliases: ["personal_information.phone", "phone"], always: true, get: (v) => v.personal_information?.phone },
  { aliases: ["personal_information.location", "location"], always: true, get: (v) => v.personal_information?.location },
  { aliases: ["personal_information.linkedin", "linkedin"], get: (v) => v.personal_information?.linkedin },
  { aliases: ["personal_information.website", "website", "portfolio"], get: (v) => v.personal_information?.portfolio },
  { aliases: ["summary", "personal_information.summary"], get: (v) => v.personal_information?.summary },
  { aliases: ["skills", "skills.list", "list"], get: (v) => v.skills?.list },
  { aliases: ["languages"], get: (v) => v.skills?.languages },
  { aliases: ["total_experience", "compensation.total_experience"], get: (v) => v.compensation?.total_experience },
  { aliases: ["current_salary", "compensation.current_salary"], salaryGated: true, get: (v) => v.compensation?.current_salary },
  { aliases: ["expected_salary", "compensation.expected_salary"], salaryGated: true, get: (v) => v.compensation?.expected_salary },
  { aliases: ["notice_period", "compensation.notice_period"], salaryGated: true, get: (v) => v.compensation?.notice_period },
];

// Same idea, but for fields that live inside a repeating section — each
// getter runs once per row (e.g. once per work-experience entry).
const REPEATING_MANDATORY_FIELD_MAP = {
  experience: [
    { aliases: ["work_experience.title", "experience.jobTitle", "jobTitle", "title"], get: (e) => e?.jobTitle },
    { aliases: ["work_experience.company", "experience.company", "company"], get: (e) => e?.company },
    { aliases: ["work_experience.start_date", "experience.startDate", "startDate"], get: (e) => e?.startDate },
    { aliases: ["work_experience.end_date", "experience.endDate", "endDate"], get: (e) => e?.endDate },
    { aliases: ["work_experience.location", "experience.location", "location"], get: (e) => e?.location },
    { aliases: ["work_experience.description", "experience.description", "description"], get: (e) => e?.description },
  ],
  education: [
    { aliases: ["education.institution", "institution"], get: (e) => e?.institution },
    { aliases: ["education.degree", "degree"], get: (e) => e?.degree },
    { aliases: ["education.field_of_study", "fieldOfStudy"], get: (e) => e?.fieldOfStudy },
    { aliases: ["education.start_date", "startDate"], get: (e) => e?.startDate },
    { aliases: ["education.end_date", "endDate"], get: (e) => e?.endDate },
    { aliases: ["education.grade", "grade"], get: (e) => e?.grade },
  ],
};

/**
 * How many fields the form will refuse to save blank are still blank in `values`.
 *
 * @param {object} values - the watched form values
 * @param {string[]} mandatoryFields - the job's field config
 * @param {boolean} [salaryFieldsRequired] - the same flag passed to buildReviewSchema
 */
export const countMissingMandatoryFields = (values = {}, mandatoryFields = [], salaryFieldsRequired = true) => {
  let count = 0;

  MANDATORY_FIELD_MAP.forEach(({ aliases, always, salaryGated, get }) => {
    if (salaryGated && !salaryFieldsRequired) return;
    if (!always && !fieldMatches(mandatoryFields, aliases)) return;
    if (isBlankValue(get(values))) count += 1;
  });

  Object.entries(REPEATING_MANDATORY_FIELD_MAP).forEach(([sectionKey, fields]) => {
    const rows = Array.isArray(values[sectionKey]) ? values[sectionKey] : [];
    rows.forEach((row) => {
      fields.forEach(({ aliases, get }) => {
        if (fieldMatches(mandatoryFields, aliases) && isBlankValue(get(row))) count += 1;
      });
    });
  });

  return count;
};

/**
 * The label and tone for a form pane's status chip. Mandatory fields come
 * first because they are the common blocker; a filled-but-wrong field is the
 * next thing to say, and only then is the form ready.
 *
 * @param {object} values - the watched form values
 * @param {string[]} mandatoryFields - the job's field config
 * @param {object} errors - react-hook-form's formState.errors
 * @param {object} [options]
 * @param {boolean} [options.salaryFieldsRequired] - the same flag passed to buildReviewSchema
 * @param {string} [options.readyLabel] - what to say when nothing is blocking
 */
export const buildSaveStatus = (values, mandatoryFields, errors, { salaryFieldsRequired = true, readyLabel = "Ready to save" } = {}) => {
  const missing = countMissingMandatoryFields(values || {}, mandatoryFields || [], salaryFieldsRequired);
  if (missing > 0) {
    return { label: `${missing} mandatory field${missing !== 1 ? "s" : ""} left`, tone: "amber", blocking: true };
  }
  if (hasErrorTree(errors)) {
    return { label: "Fix the highlighted fields", tone: "rose", blocking: true };
  }
  return { label: readyLabel, tone: "emerald", blocking: false };
};
